// Domain types shared by the server, web client and desktop client.
// Dates cross the wire as ISO-8601 strings.

export type ISODate = string;
export type UUID = string;

// ---------- App account ----------

export interface User {
  id: UUID;
  email: string;
  displayName: string;
  createdAt: ISODate;
  /** How this person can sign in: 'password' and/or Firebase providers such as 'google.com'. */
  authProviders: string[];
  /** Password sign-in also asks for an authenticator-app code. */
  twoFactorEnabled: boolean;
}

export type DeviceKind = 'desktop' | 'web' | 'mobile-web';

export interface DeviceSession {
  id: UUID;
  deviceName: string;
  deviceKind: DeviceKind;
  userAgent: string | null;
  createdAt: ISODate;
  lastSeenAt: ISODate;
  current: boolean;
}

export type ThemeMode = 'light' | 'dark' | 'system';

/** Accent colour preset applied to buttons, selection, highlights and the notification island. */
export type AccentId = 'ocean' | 'violet' | 'emerald' | 'teal' | 'sunset' | 'rose' | 'indigo' | 'graphite';
export type SidebarStyle = 'light' | 'tinted' | 'bold';
export type Density = 'comfortable' | 'compact';

export type IslandPosition = 'center' | 'right';
export type IslandGap = 'snug' | 'comfortable' | 'roomy';
export type IslandStyle = 'black' | 'graphite' | 'accent';
export type IslandMotion = 'smooth' | 'lively' | 'subtle';
/** Pixels between the island and the top of the screen (below the macOS menu bar). */
export const ISLAND_GAP_PX: Record<IslandGap, number> = { snug: 8, comfortable: 16, roomy: 28 };

/** What the notification island shows. Personal (per person, not per workspace). */
export interface NotificationPrefs {
  island: boolean; // master switch for the island
  newMail: boolean;
  ai: boolean; // AI drafts ready, analysis finished
  automation: boolean; // automatic replies sent / held, reminders
  system: boolean; // send failures, mailboxes needing reconnection, workspace changes
  sound: boolean; // short soft chime when the island appears
  desktopScreenIsland: boolean; // desktop app: show the island on top of every app, at the top centre of the screen
  durationSeconds: number; // how long the island stays expanded (3..15)
  islandPosition: IslandPosition; // where it drops in
  islandGap: IslandGap; // space between the island and the top of the screen
  islandStyle: IslandStyle; // card colour
  islandMotion: IslandMotion; // animation character
}

export interface RingSettings {
  enabled: boolean;
  intervalMinutes: number; // 60 / 120 / custom (min 5)
  ringtone: RingtoneId;
  volume: number; // 0..1
  ringDurationSeconds: number; // bounded sound session length
  snoozeMinutes: number;
}

export type RingtoneId = 'chime' | 'bell' | 'pulse' | 'marimba' | 'alarm';

export interface QuietHours {
  enabled: boolean;
  start: string; // "22:00"
  end: string; // "07:00"
}

export interface AiPrivacySettings {
  consentGivenAt: ISODate | null; // consent to transfer email content to AI providers
  attachmentConsentGivenAt: ISODate | null;
  excludedSenders: string[]; // addresses or @domains never sent to AI
  excludedTopics: string[]; // keywords; matching threads are never sent to AI
  redactPatterns: RedactionKind[];
  /** Which of the server's built-in AI engines this workspace uses (null = the server default). Ignored once the workspace has a provider of its own. */
  builtinEngine: AiProviderKind | null;
}

export type RedactionKind = 'card_numbers' | 'iban' | 'phone_numbers' | 'national_ids' | 'passwords';

export interface UserSettings {
  theme: ThemeMode;
  fontScale: number; // 0.875..1.375
  timezone: string; // IANA
  globalPause: boolean; // stops every automatic send immediately
  ring: RingSettings;
  quietHours: QuietHours;
  ai: AiPrivacySettings;
  retentionDays: number | null; // null = keep forever
  blockRemoteImages: boolean;
  lastInboxReviewAt: ISODate | null;
  // ----- personal (always stored on the person, never shared by a workspace) -----
  accent: AccentId;
  sidebarStyle: SidebarStyle;
  density: Density;
  notifications: NotificationPrefs;
  notificationsSeenAt: ISODate | null;
  /** When this person finished (or skipped) first-time setup; null shows the setup wizard. */
  onboardedAt: ISODate | null;
  /** Seconds a sent message waits before it goes out, so it can be undone (0 = off). */
  undoSendSeconds: number;
  /** Morning email: what needs you across your workspaces. Sent at `hour` in your time zone. */
  dailySummary: { enabled: boolean; hour: number };
}

/** Settings keys that belong to the person rather than the workspace they are working in. */
export const PERSONAL_SETTING_KEYS = ['theme', 'fontScale', 'blockRemoteImages', 'accent', 'sidebarStyle', 'density', 'notifications', 'notificationsSeenAt', 'onboardedAt', 'undoSendSeconds', 'dailySummary'] as const;
export type PersonalSettingKey = (typeof PERSONAL_SETTING_KEYS)[number];

// ---------- Email categories ----------

export type CategorySource = 'user' | 'learned' | 'ai' | 'heuristic';

export interface MailCategory {
  id: UUID;
  key: string; // stored on threads; built-in keys are stable ('billing'), custom ones start with 'c_'
  name: string;
  description: string; // what belongs here — the AI follows this
  color: string;
  builtin: boolean;
  hidden: boolean; // hidden categories are never assigned
  position: number;
  total: number; // inbox conversations in this category (visible to you)
  unread: number;
}

// ---------- Workspaces (personal / organization) ----------

export type WorkspaceKind = 'personal' | 'organization';
export type WorkspaceRole = 'owner' | 'admin' | 'member';

export interface WorkspaceSummary {
  id: UUID;
  kind: WorkspaceKind;
  name: string;
  color: string; // hex
  role: WorkspaceRole;
  memberCount: number;
}

export interface WorkspaceMember {
  userId: UUID;
  displayName: string;
  email: string;
  role: WorkspaceRole;
  joinedAt: ISODate;
  divisionIds: UUID[];
}

/** A team inside an organization. Mailboxes assigned to divisions are visible only to their members (and admins). */
export interface Division {
  id: UUID;
  name: string;
  color: string;
  description: string;
  memberIds: UUID[];
  mailboxIds: UUID[];
  createdAt: ISODate;
}

export interface WorkspaceInvite {
  id: UUID;
  code: string;
  role: Exclude<WorkspaceRole, 'owner'>;
  divisionIds: UUID[];
  email: string | null; // when set, only this address may use the invite
  uses: number;
  maxUses: number | null;
  expiresAt: ISODate | null;
  createdAt: ISODate;
  createdByName: string | null;
}

export interface WorkspaceDetail {
  workspace: WorkspaceSummary;
  members: WorkspaceMember[];
  divisions: Division[];
  invites: WorkspaceInvite[]; // empty unless you are an owner/admin
}

/** How a new account starts out. */
export type SignupWorkspace =
  | { kind: 'personal' }
  | { kind: 'organization'; name: string; divisions?: string[] }
  | { kind: 'join'; code: string };

// ---------- Notifications (dynamic island) ----------

export type NotificationKind = 'mail' | 'ai_draft' | 'ai_analysis' | 'auto_reply' | 'reminder' | 'send_failed' | 'account' | 'workspace';

export interface AppNotification {
  id: UUID;
  kind: NotificationKind;
  title: string; // e.g. sender name, "AI draft ready"
  body: string; // e.g. subject
  detail: string | null; // optional third line (snippet / reason)
  mailboxId: UUID | null;
  threadId: UUID | null;
  accountLabel: string | null; // mailbox display name
  accountColor: string | null; // mailbox colour (hex)
  count: number; // >1 when several events were grouped (e.g. 4 new emails)
  createdAt: ISODate;
  /** Set for project mail: the project, and the sound the recipient chose for it. */
  projectId?: UUID | null;
  projectName?: string | null;
  tone?: NotificationTone | null;
}

/** Notification sounds a person can pick per project. */
export type NotificationTone = 'chime' | 'bell' | 'pop' | 'marimba' | 'ping' | 'none';
export const NOTIFICATION_TONES: { id: NotificationTone; label: string }[] = [
  { id: 'chime', label: 'Chime' },
  { id: 'bell', label: 'Bell' },
  { id: 'pop', label: 'Pop' },
  { id: 'marimba', label: 'Marimba' },
  { id: 'ping', label: 'Ping' },
  { id: 'none', label: 'Silent' },
];

// ---------- Projects ----------

/** Cover of a project card: a preset gradient, or an uploaded image. */
export type ProjectCover = { kind: 'gradient'; value: string } | { kind: 'image'; value: string };
export const PROJECT_GRADIENTS: Record<string, string> = {
  ocean: 'linear-gradient(135deg, #22B4FF 0%, #0A5CF2 55%, #0A2BA6 100%)',
  sunset: 'linear-gradient(135deg, #FDBA74 0%, #F97316 50%, #BE123C 100%)',
  forest: 'linear-gradient(135deg, #86EFAC 0%, #16A34A 55%, #14532D 100%)',
  grape: 'linear-gradient(135deg, #C4B5FD 0%, #7C3AED 55%, #3B0764 100%)',
  rose: 'linear-gradient(135deg, #FBCFE8 0%, #EC4899 55%, #831843 100%)',
  slate: 'linear-gradient(135deg, #CBD5E1 0%, #475569 55%, #0F172A 100%)',
  gold: 'linear-gradient(135deg, #FDE68A 0%, #F59E0B 55%, #92400E 100%)',
  teal: 'linear-gradient(135deg, #99F6E4 0%, #0D9488 55%, #134E4A 100%)',
};

export type ProjectRuleKind = 'sender' | 'domain' | 'subject';
export interface ProjectRule {
  id: UUID;
  kind: ProjectRuleKind;
  value: string;
}

export interface ProjectMember {
  userId: UUID;
  name: string;
  email: string;
  role: 'manager' | 'member';
}

/** A note on the project itself, written by one of its people. */
export interface ProjectNote {
  id: UUID;
  body: string;
  author: { id: UUID; name: string; email: string } | null;
  createdAt: ISODate;
}

/** An attachment from one of the project's conversations. */
export interface ProjectFile {
  id: UUID;
  filename: string;
  mimeType: string;
  size: number;
  previewKind: 'image' | 'pdf' | 'text' | 'none';
  threadId: UUID;
  subject: string;
  from: string;
  at: ISODate;
}

export interface ProjectSummary {
  id: UUID;
  name: string;
  description: string;
  cover: ProjectCover;
  members: ProjectMember[];
  rules: ProjectRule[];
  conversationCount: number;
  /** Conversations with new mail since you last opened them. */
  unreadCount: number;
  lastActivityAt: ISODate | null;
  /** What you can do: managers/owners/admins edit; members view and follow up. */
  canManage: boolean;
  /** Your own preferences for this project (null when you aren't a member). */
  me: { notify: boolean; tone: NotificationTone } | null;
  createdAt: ISODate;
}

// ---------- Mailboxes ----------

export type MailProvider = 'gmail' | 'outlook' | 'imap' | 'demo';

export type MailboxStatus = 'connecting' | 'active' | 'needs_reauth' | 'error' | 'disconnected';

export interface ConnectorCapabilities {
  pushNotifications: boolean; // provider change notifications
  incrementalSync: boolean; // history/delta tokens
  serverThreads: boolean; // provider supplies thread ids
  labels: boolean; // labels vs folders
  sendAs: boolean; // send via provider API
  contactsApi: boolean;
  limitations: string[]; // human-readable limitations shown in UI
}

export interface HistoryScope {
  mode: 'full' | 'range';
  from: ISODate | null;
  to: ISODate | null;
  excludedFolderIds: UUID[];
  excludedSenders: string[];
}

export interface ImportProgress {
  phase: 'idle' | 'recent' | 'history' | 'complete' | 'paused' | 'error';
  processed: number;
  estimatedTotal: number | null;
  oldestImportedAt: ISODate | null;
  newestImportedAt: ISODate | null;
  startedAt: ISODate | null;
  completedAt: ISODate | null;
  error: string | null;
}

export interface Mailbox {
  id: UUID;
  provider: MailProvider;
  email: string;
  displayName: string; // friendly account name e.g. "Work"
  senderName: string; // name used in From header
  color: string; // hex
  isDefaultSender: boolean;
  status: MailboxStatus;
  statusReason: string | null;
  lastSyncAt: ISODate | null;
  automationActivatedAt: ISODate | null; // automation only applies to mail received after this
  historyScope: HistoryScope;
  importProgress: ImportProgress;
  capabilities: ConnectorCapabilities;
  isDemo: boolean;
  createdAt: ISODate;
  divisionIds: UUID[]; // organization divisions this mailbox is restricted to (empty = everyone in the workspace)
}

export interface Folder {
  id: UUID;
  mailboxId: UUID;
  name: string;
  role: FolderRole;
  unreadCount: number;
  excluded: boolean;
}

export type FolderRole = 'inbox' | 'sent' | 'drafts' | 'trash' | 'archive' | 'spam' | 'custom';

// ---------- Mail ----------

export interface Address {
  name: string | null;
  email: string;
}

export interface ThreadSummary {
  id: UUID;
  mailboxId: UUID;
  subject: string;
  snippet: string;
  participants: Address[];
  lastMessageAt: ISODate;
  lastSender: Address | null;
  messageCount: number;
  unread: boolean;
  flagged: boolean;
  hasAttachments: boolean;
  folderRoles: FolderRole[];
  awaitingReplyFrom: 'me' | 'them' | 'nobody' | 'unknown';
  hasPendingDraft: boolean;
  attentionPending: boolean;
  category: string | null; // MailCategory.key
  categorySource: CategorySource | null;
  /** Organizations: the member who owns this conversation. */
  assignedTo: { id: UUID; name: string; email: string } | null;
  /** "Remind me if no reply": set and not yet answered. `due` once the time has passed. */
  followUp: { at: ISODate; due: boolean } | null;
  /** Organizations: projects this conversation belongs to, and an AI suggestion when it matches none. */
  projects?: { id: UUID; name: string }[];
  suggestedProject?: { id: UUID; name: string; reason: string | null } | null;
}

export interface MessageView {
  id: UUID;
  threadId: UUID;
  mailboxId: UUID;
  from: Address;
  to: Address[];
  cc: Address[];
  subject: string;
  date: ISODate;
  isOutgoing: boolean;
  isAutomated: boolean;
  automatedReasons: string[];
  bodyText: string;
  bodyHtml: string | null; // sanitised server-side, scripts removed, remote images rewritten/blocked
  visibleText: string; // body without quoted history and signature
  quotedText: string | null;
  signatureText: string | null;
  hasRemoteImages: boolean;
  attachments: AttachmentView[];
  unread: boolean;
  importedVia: 'history' | 'live';
}

export type AttachmentStatus = 'pending' | 'stored' | 'parsing' | 'parsed' | 'partial' | 'unsupported' | 'encrypted' | 'unreadable' | 'too_large' | 'failed';

export interface AttachmentSummary {
  keyPoints: string[];
  dates: { value: string; context: string; ref: string | null }[];
  amounts: { value: string; context: string; ref: string | null }[];
  pages: number | null;
  sheets: string[] | null;
  limitations: string[]; // parsing limitations disclosed to the user
  generatedBy: string | null; // "provider/model" or "extractor"
}

export interface AttachmentView {
  id: UUID;
  messageId: UUID;
  filename: string;
  mimeType: string;
  size: number;
  status: AttachmentStatus;
  statusDetail: string | null;
  summary: AttachmentSummary | null;
  previewKind: 'image' | 'pdf' | 'text' | 'none';
}

// ---------- AI analysis ----------

export interface Evidence {
  messageId: UUID;
  quote: string;
}

export interface Finding {
  kind:
    | 'unanswered_question'
    | 'missing_document'
    | 'pending_approval'
    | 'requested_information'
    | 'promise'
    | 'deadline'
    | 'stalled'
    | 'follow_up';
  text: string;
  owner: 'me' | 'them' | 'unknown';
  basis: 'observed' | 'inferred';
  dueDate: ISODate | null;
  evidence: Evidence[];
}

export interface ThreadAnalysis {
  threadId: UUID;
  about: string;
  latestPosition: string;
  nextReplyOwner: 'me' | 'them' | 'nobody' | 'unknown';
  nextReplyReason: string;
  findings: Finding[];
  nextActions: string[];
  category: string | null;
  language: string | null;
  analysedMessageCount: number;
  totalMessageCount: number;
  insufficientEvidence: boolean;
  generatedBy: string;
  generatedAt: ISODate;
}

export interface CoverageReport {
  mailboxes: {
    mailboxId: UUID;
    email: string;
    importedMessages: number;
    oldestImportedAt: ISODate | null;
    newestImportedAt: ISODate | null;
    phase: ImportProgress['phase'];
    analysedThreads: number;
    totalThreads: number;
  }[];
}

export interface InboxAnswer {
  answer: string;
  citations: { threadId: UUID; messageId: UUID; subject: string; quote: string }[];
  coverageNote: string;
  basedOnMessages: number;
  generatedBy: string;
}

export interface InboxInsights {
  period: { from: ISODate; to: ISODate };
  topics: { label: string; count: number; threadIds: UUID[] }[];
  needsMyReply: { threadId: UUID; subject: string; waitingSince: ISODate }[];
  stalled: { threadId: UUID; subject: string; lastActivity: ISODate }[];
  changedSinceLastReview: { newThreads: number; newMessages: number; since: ISODate | null };
  coverage: CoverageReport;
}

// ---------- AI providers ----------

export type AiProviderKind = 'openai' | 'anthropic' | 'gemini' | 'openrouter' | 'openai_compatible' | 'local_agent' | 'demo';

export type AiTask = 'draft' | 'analysis' | 'attachment' | 'question';

export interface AiProviderConfig {
  id: UUID;
  kind: AiProviderKind;
  label: string;
  endpoint: string | null;
  defaultModel: string;
  hasApiKey: boolean; // keys are never returned to clients
  apiKeyHint: string | null; // e.g. "sk-…a1b2"
  timeoutMs: number;
  monthlyBudgetUsd: number | null;
  spentThisMonthUsd: number;
  fallbackProviderId: UUID | null;
  fallbackModel: string | null;
  allowFallback: boolean; // user explicitly permits switching to fallback
  lastTestAt: ISODate | null;
  lastTestOk: boolean | null;
  lastTestMessage: string | null;
  createdAt: ISODate;
}

/** The server operator's built-in provider, used by workspaces that haven't added their own. Its key is never sent. */
export interface AiBuiltinInfo {
  label: string;
  /** The engine this workspace currently uses (its `builtinEngine` setting, or the server default). */
  kind: AiProviderKind;
  model: string;
  /** Every engine the server offers; more than one means the workspace can switch in Settings → AI. */
  engines: { kind: AiProviderKind; label: string; model: string }[];
  defaultKind: AiProviderKind;
  monthlyLimitUsd: number | null; // null = unlimited
  spentThisMonthUsd: number;
  inUse: boolean; // true while this workspace has no provider of its own
}

export interface AiTaskAssignment {
  task: AiTask;
  providerId: UUID | null;
  model: string | null;
}

// ---------- Responder ----------

export type ResponseMode = 'suggest' | 'acknowledge' | 'auto_reply';

export interface BusinessHours {
  enabled: boolean;
  days: number[]; // 0=Sun..6=Sat
  start: string; // "09:00"
  end: string; // "17:00"
  timezone: string;
  outsideOnly: boolean; // true => rule only matches outside business hours
}

export interface RuleConditions {
  senders: string[]; // exact addresses
  domains: string[]; // example.com
  subjectPhrases: string[];
  bodyPhrases: string[];
  categories: string[]; // AI categories e.g. "support", "sales"
  languages: string[]; // ISO 639-1
  businessHours: BusinessHours;
  excludeSenders: string[];
  excludeDomains: string[];
  excludeSubjectPhrases: string[];
}

export interface ResponderRule {
  id: UUID;
  mailboxId: UUID | null; // null = all mailboxes
  name: string;
  enabled: boolean;
  mode: ResponseMode;
  priority: number; // lower runs first
  conditions: RuleConditions;
  ackTemplate: string | null;
  autoReplyInstructions: string | null; // facts the AI may use for controlled auto replies
  tone: string | null;
  maxRepliesPerSenderPerDay: number;
  cooldownMinutes: number;
  requireApproval: boolean; // for auto_reply: queue for approval rather than sending
  allowedAttachmentIds: UUID[]; // library files this rule may auto-send
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface RuleEvaluation {
  ruleId: UUID;
  ruleName: string;
  matched: boolean;
  reasons: string[]; // why it matched or did not
}

export interface DryRunResult {
  messageId: UUID;
  eligible: boolean;
  blockedBy: string[]; // safety reasons (no-reply, list, bounce, auto-submitted, history import, paused...)
  evaluations: RuleEvaluation[];
  wouldDo: 'nothing' | 'suggest' | 'acknowledge' | 'auto_reply' | 'auto_reply_pending_approval';
  matchedRuleId: UUID | null;
}

export type DraftKind = 'suggestion' | 'user' | 'acknowledgement' | 'auto_reply';

export type DraftStatus =
  | 'draft'
  | 'pending_attachment'
  | 'pending_approval'
  | 'scheduled'
  | 'queued'
  | 'sending'
  | 'provider_accepted'
  | 'failed'
  | 'cancelled'
  | 'stale';

export interface RequiredAttachment {
  description: string;
  reason: string;
  requestedInMessageId: UUID | null;
  suggestedLibraryFileId: UUID | null;
  resolvedLibraryFileId: UUID | null;
  resolvedUploadId: UUID | null;
  deliverAs: 'link' | 'attachment' | null;
}

export interface Draft {
  id: UUID;
  mailboxId: UUID;
  threadId: UUID | null;
  inReplyToMessageId: UUID | null;
  kind: DraftKind;
  status: DraftStatus;
  to: Address[];
  cc: Address[];
  bcc: Address[];
  subject: string;
  bodyText: string;
  explanation: string | null;
  supportingContext: Evidence[];
  missingInformation: string[];
  requiredAttachments: RequiredAttachment[];
  signatureId: UUID | null;
  scheduledAt: ISODate | null;
  ruleId: UUID | null;
  generatedBy: string | null;
  error: string | null;
  version: number;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export type SendStatus = 'queued' | 'sending' | 'provider_accepted' | 'failed' | 'unknown' | 'cancelled';

export interface ReviewItem {
  id: UUID;
  sendRecordId: UUID;
  kind: 'acknowledgement' | 'auto_reply';
  mailboxId: UUID;
  mailboxEmail: string;
  ruleId: UUID | null;
  ruleName: string | null;
  originalMessage: { id: UUID; threadId: UUID; from: Address; subject: string; date: ISODate; excerpt: string };
  response: { to: Address[]; subject: string; bodyText: string; attachments: { name: string; via: 'link' | 'attachment' }[] };
  status: SendStatus;
  deliveryNote: string; // e.g. "Accepted by Gmail. Delivery to recipient is not confirmed."
  sentAt: ISODate | null;
  reviewedAt: ISODate | null;
  attentionItemId: UUID | null;
}

// ---------- Attention / ringing ----------

export type AttentionStatus = 'pending' | 'snoozed' | 'acknowledged';

export interface AttentionItem {
  id: UUID;
  sourceType: 'auto_send' | 'attachment_request' | 'send_failure' | 'reauth';
  sourceId: UUID;
  threadId: UUID | null;
  mailboxId: UUID | null;
  title: string;
  body: string;
  status: AttentionStatus;
  nextRingAt: ISODate | null;
  snoozedUntil: ISODate | null;
  ringCount: number;
  lastRangAt: ISODate | null;
  acknowledgedAt: ISODate | null;
  acknowledgedByDevice: string | null;
  createdAt: ISODate;
}

export interface RingDirective {
  // returned to a device that holds the ringing lease
  shouldRing: boolean;
  items: AttentionItem[];
  leaseHeldBy: string | null; // device name currently responsible for sound
  leaseExpiresAt: ISODate | null;
  quietHoursActive: boolean;
  nextDueAt: ISODate | null;
}

// ---------- Attachments library ----------

export interface LibraryFile {
  id: UUID;
  friendlyName: string;
  filename: string;
  mimeType: string;
  size: number;
  version: number;
  contentHash: string;
  approved: boolean;
  allowedRecipients: string[];
  allowedDomains: string[];
  expiresAt: ISODate | null;
  sensitive: boolean;
  autoSendAllowed: boolean;
  source: 'upload' | 'sent_history';
  historicalUseCount: number;
  supersededById: UUID | null;
  createdAt: ISODate;
}

export interface LibrarySuggestion {
  // a frequently reused file detected in sent history — a suggestion, never a permission
  filename: string;
  contentHash: string;
  timesSent: number;
  lastSentAt: ISODate;
  recipients: string[];
  sampleAttachmentId: UUID;
  alreadyInLibrary: boolean;
}

export interface ShareLink {
  id: UUID;
  libraryFileId: UUID;
  recipientEmail: string | null;
  url: string | null; // only returned at creation
  expiresAt: ISODate;
  revokedAt: ISODate | null;
  accessCount: number;
  lastAccessAt: ISODate | null;
  createdAt: ISODate;
}

export interface AttachmentRequest {
  id: UUID;
  draftId: UUID;
  threadId: UUID | null;
  messageId: UUID | null;
  description: string;
  reason: string;
  requestedBy: Address | null;
  status: 'open' | 'fulfilled' | 'cancelled';
  suggestedLibraryFileId: UUID | null;
  createdAt: ISODate;
}

// ---------- Personalisation ----------

export interface AiPreferences {
  scope: 'global' | 'mailbox';
  mailboxId: UUID | null;
  writingStyle: string;
  language: string;
  tone: string;
  greeting: string;
  signOff: string;
  responseLength: 'short' | 'medium' | 'detailed';
  businessDetails: string;
  faqs: { question: string; answer: string }[];
}

export interface StyleExample {
  id: UUID;
  mailboxId: UUID | null;
  kind: 'reply_example' | 'correction';
  context: string; // what the reply responded to (short)
  original: string | null; // AI draft before correction
  text: string; // approved text
  createdAt: ISODate;
}

// ---------- Signatures & contacts ----------

export interface Signature {
  id: UUID;
  mailboxId: UUID | null;
  name: string;
  text: string;
  html: string | null; // sanitised
  isDefaultNew: boolean;
  isDefaultReply: boolean;
  updatedAt: ISODate;
}

export interface Contact {
  id: UUID;
  name: string;
  emails: string[];
  company: string | null;
  notes: string;
  tags: string[];
  favorite: boolean;
  source: 'manual' | 'conversation' | 'csv' | 'suggestion' | 'provider';
  lastInteractionAt: ISODate | null;
  interactionCount: number;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface ContactSuggestion {
  email: string;
  name: string | null;
  messageCount: number;
  lastInteractionAt: ISODate;
}

// ---------- Audit ----------

export interface AuditEntry {
  id: UUID;
  actor: 'user' | 'system' | 'rule' | 'device';
  action: string;
  entityType: string;
  entityId: UUID | null;
  details: Record<string, unknown>;
  /** The person who acted, when a signed-in person caused it (null for background work). */
  actorName: string | null;
  actorEmail: string | null;
  createdAt: ISODate;
}

/** A private team comment on a conversation (organizations). Never sent to anyone outside the team. */
export interface TeamNote {
  id: UUID;
  body: string;
  author: { id: UUID; name: string; email: string } | null;
  createdAt: ISODate;
}

/** Someone currently writing a reply to a conversation. */
export interface TeamPresence {
  id: UUID;
  name: string;
  email: string;
  since: ISODate;
}

/** Who in the workspace has opened a conversation and who replied to it (organizations). */
export interface ThreadActivity {
  readBy: { name: string; email: string; at: ISODate }[];
  repliedBy: { name: string | null; email: string | null; kind: 'manual' | 'acknowledgement' | 'auto_reply'; at: ISODate }[];
}

// ---------- Realtime ----------

export type ServerEvent =
  | { type: 'mailbox.updated'; mailboxId: UUID }
  | { type: 'import.progress'; mailboxId: UUID; progress: ImportProgress }
  | { type: 'threads.changed'; mailboxId: UUID; threadIds: UUID[] }
  | { type: 'draft.updated'; draftId: UUID; threadId: UUID | null }
  | { type: 'analysis.updated'; threadId: UUID }
  | { type: 'attention.changed' }
  | { type: 'review.created'; reviewId: UUID }
  | { type: 'settings.changed' }
  | { type: 'session.revoked'; sessionId: UUID }
  | { type: 'attachment.updated'; attachmentId: UUID }
  | { type: 'notify'; notification: AppNotification }
  | { type: 'workspace.changed' }
  | { type: 'thread.presence'; threadId: UUID; mailboxId: UUID; replying: TeamPresence[] };
